import React, { useState } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import { useDiscounts } from '../../hooks/useDiscounts';
import {
  colors,
  hairline,
  inputText,
  radii,
  screenPadding,
  spacing,
} from '../../theme';
import BottomSheet from '../ui/BottomSheet';
import Button from '../ui/Button';
import EmptyState from '../ui/EmptyState';
import Icon from '../ui/Icon';
import Text from '../ui/Text';

/** Whole rupees, grouped - `Intl` is not reliably present on Android JSC. */
const rupees = (value: number) =>
  '₹' + String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

type Props = {
  visible: boolean;
  /** The basket's item total - used only to soften a card whose minimum looks
      out of reach, never to work out a discount. See the note below. */
  itemTotal: number;
  /** The code currently applied - typed, or picked from the list. */
  appliedCode: string | null;
  /**
   * Why the applied code did not price, from the cart's latest quote.
   *
   * `null`/`undefined` covers two different truths the sheet does not need to
   * tell apart: nothing has been tried yet, or the last thing tried worked.
   * Either way there is nothing to show here.
   */
  refusal?: { message: string } | null;
  onClose: () => void;
  /**
   * Apply a code - from the list or typed freehand. Only sets what the cart
   * asks the server about; whether it actually prices anything is answered by
   * the next `checkout/quote`, not by this call.
   */
  onApply: (code: string) => void;
  onRemove: () => void;
};

/**
 * Offers, as a sheet over the cart.
 *
 * ---------------------------------------------------------------------------
 * EVERYTHING HERE IS A CANDIDATE, NOTHING IS A GUARANTEE
 * ---------------------------------------------------------------------------
 * This used to hold a hardcoded coupon list and decide for itself whether the
 * basket qualified - a discount the client invented, applied against a code
 * the server had never heard of (see the note that used to live on
 * `data/booking.ts`'s `coupons`). It now shows whatever `GET discounts/available`
 * actually offers, and applying one only sets the CODE the cart will ask the
 * server about. Whether it prices anything, and for how much, is answered by
 * `checkout/quote` - which is also why a manually typed code is treated no
 * differently from a listed one: neither is validated here.
 *
 * The list is still the point. A bare "apply coupon" field asks someone to
 * already know a code, which most people do not - so real offers are shown,
 * each one applied by a tap, and the field is left for a code handed out
 * somewhere else, exactly as the sheet always intended.
 *
 * Coupons the basket looks short of are listed rather than hidden - knowing
 * that an offer needs a bigger basket is worth more than a shorter list - but
 * the exact shortfall is gone. `Add ₹80 more to use this` was a subtraction
 * this app had no business doing; `minOrderValue` here is only ever compared,
 * never subtracted, to soften the card. The only number this component prints
 * as a REASON something failed is `refusal.message`, which the server wrote.
 */
function CouponSheet({
  visible,
  itemTotal,
  appliedCode,
  refusal,
  onClose,
  onApply,
  onRemove,
}: Props) {
  const { data: offers, loading } = useDiscounts();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const entered = code.trim().toUpperCase();
    if (!entered) {
      setError('Enter a coupon code');
      return;
    }

    setCode('');
    setError(null);
    onApply(entered);
    onClose();
  };

  const list = offers ?? [];

  return (
    <BottomSheet visible={visible} onClose={onClose}>
      <View style={styles.header}>
        <Text variant="titleLg" style={styles.headerTitle}>
          Offers & Coupons
        </Text>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close"
          onPress={onClose}
          hitSlop={10}
        >
          <Icon
            name="plus"
            size={18}
            color={colors.inkMuted}
            strokeWidth={2.2}
            style={styles.closeGlyph}
          />
        </Pressable>
      </View>

      {/* The field first: someone arriving with a code in hand should not have
          to scroll past offers they already decided against. */}
      <View style={styles.form}>
        <TextInput
          value={code}
          onChangeText={next => {
            setCode(next.toUpperCase());
            setError(null);
          }}
          placeholder="Enter coupon code"
          placeholderTextColor={colors.inkSubtle}
          autoCapitalize="characters"
          autoCorrect={false}
          style={styles.input}
          accessibilityLabel="Coupon code"
          returnKeyType="done"
          onSubmitEditing={submit}
        />
        <Button label="Apply" onPress={submit} />
      </View>

      {error ? (
        <Text variant="caption" color={colors.danger} style={styles.error}>
          {error}
        </Text>
      ) : null}

      {/* The server's own reason a code did not apply, verbatim - never a
          sentence this component assembled from a comparison of its own. */}
      {!error && refusal ? (
        <Text variant="caption" color={colors.danger} style={styles.error}>
          {refusal.message}
        </Text>
      ) : null}

      {appliedCode && !refusal ? (
        <View style={styles.applied}>
          <Icon name="checkCircle" size={16} color={colors.primary} />
          <Text variant="labelSm" color={colors.primary} style={styles.appliedText}>
            {appliedCode} applied
          </Text>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`Remove ${appliedCode}`}
            onPress={onRemove}
            hitSlop={8}
          >
            <Text variant="labelSm" color={colors.inkMuted}>
              Remove
            </Text>
          </Pressable>
        </View>
      ) : null}

      <ScrollView
        contentContainerStyle={styles.list}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {list.length > 0 ? (
          <Text
            variant="labelSm"
            color={colors.inkMuted}
            style={styles.listLabel}
          >
            Available offers
          </Text>
        ) : null}

        {/*
          Nothing to show, said plainly.

          Empty whenever the customer has no offers right now, or while
          signed out - `useDiscounts` answers both the same way. A heading
          over a blank space reads as a screen that failed to load; this says
          the true thing and points at the field above, which is still the
          way in for a code handed out by support.
        */}
        {!loading && list.length === 0 ? (
          <EmptyState
            compact
            icon="tag"
            title="No offers right now"
            message="You have no offers available today. Have a code? Enter it above."
          />
        ) : null}

        {list.map(offer => {
          /* Automatic offers have nothing to type - the quote applies them on
             its own when the basket qualifies, so there is no "Apply" here to
             offer and no code for `appliedCode` to ever match. */
          const automatic = offer.code === null;
          const isApplied =
            !automatic &&
            appliedCode !== null &&
            appliedCode.trim().toUpperCase() === offer.code?.toUpperCase();

          /* A HINT, not a gate. Comparing against the server's own minimum is
             fine; the button still works either way, because only the quote
             can say for certain whether this basket qualifies. */
          const minimum = offer.minOrderValue ? Number(offer.minOrderValue) : null;
          const looksShort = minimum !== null && itemTotal < minimum;

          return (
            <View key={offer.code ?? offer.description} style={styles.offer}>
              <View style={styles.offerHead}>
                {offer.code ? (
                  <View style={styles.tag}>
                    <Text variant="labelSm" color={colors.primary}>
                      {offer.code}
                    </Text>
                  </View>
                ) : (
                  <Text variant="caption" color={colors.inkMuted}>
                    Applied automatically
                  </Text>
                )}

                {automatic ? null : isApplied ? (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={`Remove ${offer.code}`}
                    onPress={onRemove}
                    hitSlop={8}
                  >
                    <Text variant="labelSm" color={colors.inkMuted}>
                      Remove
                    </Text>
                  </Pressable>
                ) : (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={`Apply ${offer.code}`}
                    onPress={() => {
                      onApply(offer.code as string);
                      onClose();
                    }}
                    hitSlop={8}
                  >
                    <Text variant="labelSm" color={colors.primary}>
                      Apply
                    </Text>
                  </Pressable>
                )}
              </View>

              {/* The server's own customer-facing sentence, verbatim - see
                  `Discount.description`, and the note beside it on why the
                  rule's internal ops name is not drawn above this. */}
              <Text variant="subtitle">{offer.description}</Text>
              {looksShort && minimum !== null ? (
                <Text variant="caption" color={colors.inkSubtle}>
                  Minimum order {rupees(Math.round(minimum))}
                </Text>
              ) : null}
            </View>
          );
        })}
      </ScrollView>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
  },
  headerTitle: {
    flex: 1,
  },
  // A rotated plus - the icon set has no cross glyph.
  closeGlyph: {
    transform: [{ rotate: '45deg' }],
  },

  form: {
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.sm,
  },
  input: {
    height: 46,
    paddingHorizontal: spacing.md,
    borderRadius: radii.sm,
    backgroundColor: colors.surfaceMuted,
    ...hairline,
    ...inputText,
  },
  error: {
    paddingHorizontal: screenPadding,
    paddingTop: spacing.sm,
  },
  applied: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginHorizontal: screenPadding,
    marginTop: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceAccent,
  },
  appliedText: {
    flex: 1,
  },

  list: {
    paddingBottom: spacing.md,
  },
  listLabel: {
    paddingHorizontal: screenPadding,
    paddingTop: spacing.lg,
    paddingBottom: spacing.sm,
  },
  offer: {
    gap: spacing.xs,
    marginHorizontal: screenPadding,
    marginBottom: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surface,
    ...hairline,
  },
  offerHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  /* Dashed, so the code reads as something torn off a coupon rather than as
     another button on a card that already has one. */
  tag: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radii.sm,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.borderStrong,
  },
});

export default CouponSheet;
